/**
 * Formatter for MCP tool descriptions.
 *
 * MCP servers (esp. Calendly) ship descriptions that look like this:
 *
 *   Get current user
 *
 *   Returns basic information about your user account.  <!-- theme: info -->
 *
 *     > #### Required scopes: `users:read`
 *
 *   :param uuid: User unique identifier, or the constant "me" ...
 *   :type uuid: str
 *   :return: Returns the result object.
 *
 * Rendering this raw in a table cell produces a wall of symbols.
 * This utility parses the blob into four semantic parts:
 *
 *   - `title`     first non-empty line
 *   - `summary`   remaining free-text paragraphs (HTML-comments / markdown-
 *                 blockquotes / docstring metadata removed)
 *   - `scopes`    strings extracted from "Required scopes: `a`, `b`"
 *   - `params`    array of { name, required, description } extracted from
 *                 `:param name:` lines (combined with `:type name:` if present)
 *
 * Also exports `<FormattedDescription text={...} />` which renders all parts
 * as a compact, readable block using Tailwind classes.
 */

import { FC } from 'react';

export interface ParsedMethodDescription {
  title: string;
  summary: string;
  scopes: string[];
  params: Array<{ name: string; required: boolean; description: string }>;
}

// Inline (not anchored to line start) so it also matches Calendly's style
// where `> #### Required scopes:` appears mid-paragraph after a comment.
const SCOPE_BLOCK_RE =
  />\s*#{0,4}\s*Required scopes?:\s*([^\n]+)/i;
const SCOPE_STRIP_RE =
  />\s*#{0,4}\s*Required scopes?:[^\n]+/gi;
const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;
const PARAM_LINE_RE = /^:param\s+([^:]+?)\s*(\(required\))?\s*:\s*(.*?)$/gm;
const TYPE_LINE_RE = /^:type\s+[^:]+?:.*$/gm;
const RETURN_LINE_RE = /^:return:.*$/gm;
const RAISES_LINE_RE = /^:raises.*$/gm;
// Leftover bare blockquote / markdown heading markers that may remain on
// their own lines after stripping the scope block.
const BLOCKQUOTE_LEFTOVER_RE = /(?:^|\n)\s*>\s*#{0,4}[^\n]*/g;
const BACKTICK_RE = /`([^`]+)`/g;

export function parseMethodDescription(raw?: string): ParsedMethodDescription {
  if (!raw) {
    return { title: '', summary: '', scopes: [], params: [] };
  }

  // 1. Extract scopes (inline or line-start, with or without `>` blockquote).
  const scopes: string[] = [];
  const scopeMatch = raw.match(SCOPE_BLOCK_RE);
  if (scopeMatch) {
    const scopeLine = scopeMatch[1];
    for (const m of scopeLine.matchAll(BACKTICK_RE)) {
      scopes.push(m[1].trim());
    }
    if (scopes.length === 0) {
      scopeLine
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s.length < 80)
        .forEach((s) => scopes.push(s.replace(/[`.]+$/g, '')));
    }
  }

  // 2. Extract params.
  const params: ParsedMethodDescription['params'] = [];
  for (const m of raw.matchAll(PARAM_LINE_RE)) {
    const name = m[1].trim();
    const required = !!m[2];
    const description = m[3].trim();
    if (name.length > 0) {
      params.push({ name, required, description });
    }
  }

  // 3. Strip metadata to get clean free-text. Order matters: scope block
  // first (so the whole `> #### Required scopes: ...` phrase disappears),
  // then HTML comments, then leftover blockquote markers on their own lines,
  // then docstring directives.
  const cleaned = raw
    .replace(SCOPE_STRIP_RE, '')
    .replace(HTML_COMMENT_RE, '')
    .replace(BLOCKQUOTE_LEFTOVER_RE, '')
    .replace(PARAM_LINE_RE, '')
    .replace(TYPE_LINE_RE, '')
    .replace(RETURN_LINE_RE, '')
    .replace(RAISES_LINE_RE, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // 4. Split off title.
  const lines = cleaned.split('\n').map((l) => l.trim());
  const firstNonEmpty = lines.findIndex((l) => l.length > 0);
  const title = firstNonEmpty >= 0 ? lines[firstNonEmpty] : '';
  const summary =
    firstNonEmpty >= 0
      ? lines
          .slice(firstNonEmpty + 1)
          .join('\n')
          .trim()
      : '';

  return { title, summary, scopes, params };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface FormattedDescriptionProps {
  text?: string;
  compact?: boolean;
}

/**
 * Renders a parsed MCP method description with separated title, summary,
 * required-scopes chips and parameter list. Falls back to the raw text if
 * parsing yields nothing usable.
 */
export const FormattedDescription: FC<FormattedDescriptionProps> = ({
  text,
  compact = false,
}) => {
  const parsed = parseMethodDescription(text);
  const hasStructure =
    parsed.title.length > 0 ||
    parsed.summary.length > 0 ||
    parsed.scopes.length > 0 ||
    parsed.params.length > 0;

  if (!hasStructure) {
    return <span className="text-gray-400 italic">Keine Beschreibung</span>;
  }

  return (
    <div className="space-y-1.5">
      {parsed.title && (
        <div className="text-gray-800 font-medium">{parsed.title}</div>
      )}
      {parsed.summary && !compact && (
        <div className="text-gray-600 whitespace-pre-line">{parsed.summary}</div>
      )}
      {parsed.scopes.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <span className="text-gray-500 text-[11px] uppercase tracking-wide">
            Scopes:
          </span>
          {parsed.scopes.map((s) => (
            <code
              key={s}
              className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-mono"
            >
              {s}
            </code>
          ))}
        </div>
      )}
      {parsed.params.length > 0 && !compact && (
        <ul className="mt-1 space-y-0.5 pl-3 list-disc marker:text-gray-300">
          {parsed.params.map((p) => (
            <li key={p.name} className="text-gray-600">
              <code className="font-mono text-[11px] text-gray-700">{p.name}</code>
              {p.required && (
                <span className="ml-1 text-[10px] font-semibold text-amber-600 uppercase">
                  required
                </span>
              )}
              {p.description && <span className="ml-1">— {p.description}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
